import { useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { ALERT_CENTER_NS } from 'src/alerts/AlertCenter.constants';
import { formatMetricValue } from 'src/components/LeadAgentAcceptanceBanner/LeadAgentAcceptanceBanner.functions';
import ReactivateLeadAgentModal from 'src/components/LeadAgentAcceptanceBanner/modals/ReactivateLeadAgentModal/ReactivateLeadAgentModal.component';
import type { tReactivateLeadAgentModalSuccess } from 'src/components/LeadAgentAcceptanceBanner/modals/ReactivateLeadAgentModal/ReactivateLeadAgentModal.types';
import { useLeadAgentTierPricing } from 'src/components/LeadAgentAcceptanceBanner/useLeadAgentTierPricing.hook';
import { useLeadAgentAcceptanceContext } from 'src/providers/LeadAgentAcceptanceProvider/LeadAgentAcceptance.context';
import {
  LEAD_AGENT_SELECTABLE_TIERS,
  resolveSuggestedTier,
} from 'src/providers/LeadAgentAcceptanceProvider/LeadAgentAcceptance.functions';
import type { tLeadAgentSelectableTier } from 'src/providers/LeadAgentAcceptanceProvider/LeadAgentAcceptance.types';
import { useSubscriptionContext } from 'src/providers/SubscriptionProvider/Subscription.context';
import { ePlanFrequency } from 'src/providers/SubscriptionProvider/Subscription.enums';
import { eAlertType } from 'src/providers/ToastProvider/Toast.enum';
import { useToastContext } from 'src/providers/ToastProvider/Toast.hook';
import type { tAlertDetailRendererProps } from './AlertDetail.registry';
import AlertDetailBody from './AlertDetailBody.component';
import AlertOfferLine from './AlertOfferLine.component';

/** The legacy vencido banner's namespace — its dismiss copy moves verbatim. */
const EXPIRED_NS = 'common/leadAgentExpiredBanner';
/** The pre-acceptance banner's namespace — owner of the reco block copy. */
const OFFER_NS = 'common/leadAgentAcceptanceBanner';

/**
 * Rich body for the Lead Agent "vencido" alert (RET-1956): the recommended
 * plan and the REAL reactivation checkout, adopted from
 * `LeadAgentExpiredBannerContainer` after RET-1879 turned that banner off.
 *
 * This renderer exists because the family's CTA used to navigate to the Smart
 * Leadbot config page — a page with no way to pay. The money flow lives here
 * now: `ReactivateLeadAgentModal` mounts as a NESTED modal (the REL-2 pattern
 * `LeadAgentTrialCapDetail` established), so the host detail refuses to
 * dismiss itself while a charge is in flight.
 *
 * Role scoping mirrors the legacy banner: `isExpiredForOwner` is the same
 * source the alert's own predicate reads, so the Owner gets the actions and
 * the Admin reads the informative sentence alone.
 *
 * IMPACT METRICS, in the past tense (Fanny, 2026-09-02, against her prototype).
 *
 * The legacy `LeadAgentExpiredBanner` never carried them, so this is the one
 * block here that is NEW rather than adopted — she read the prototype and it
 * shows them. I had flagged the risk that a tally of what the agent used to do
 * reads as salt in the wound; her call, and the label is written to answer it:
 * the numbers arrive as the ARGUMENT for the button underneath, not as a
 * lament. Same three stats and same compact row as the pre-acceptance detail,
 * so the family reads as one thing.
 *
 * The label is the only new copy — the stats themselves reuse `miniStats.*`
 * from the offer namespace. It is past tense on purpose ("hacía por ti"): this
 * alert's whole premise is that the work already stopped, and the neighbouring
 * acceptance detail's present tense would contradict the title one line above.
 *
 * Degrades exactly like its sibling: a stat the rollup did not produce removes
 * itself, and the row disappears when none survive.
 */
export default function LeadAgentExpiredDetail(
  props: tAlertDetailRendererProps,
) {
  const { t } = useTranslation(ALERT_CENTER_NS);
  const { t: tExpired } = useTranslation(EXPIRED_NS);
  const { t: tOffer } = useTranslation(OFFER_NS);
  const { addAlert } = useToastContext();
  const { acceptance, isExpiredForOwner, markResolved, dismissExpired } =
    useLeadAgentAcceptanceContext();
  const { subscription } = useSubscriptionContext();

  const [isReactivateOpen, setIsReactivateOpen] = useState(false);
  // Synchronous re-entry latch, straight from the legacy container:
  // `dismissExpired` is async, so a rapid double-click would fire two POSTs
  // before the first resolves. This ref flips synchronously so the 2nd bails.
  const isDismissingRef = useRef(false);

  // While the reactivation modal is open the host detail must not close
  // (REL-2 by composition). Cleanup releases the lock if this renderer
  // unmounts for its own reasons (live resolution swapping the body).
  const { onNestedModalChange, onRequestClose } = props;
  useEffect(() => {
    onNestedModalChange(isReactivateOpen);
    return () => onNestedModalChange(false);
  }, [isReactivateOpen, onNestedModalChange]);

  // `resolveSuggestedTier` falls back to GROWTH, which is right for the modal
  // (it must preselect something) and wrong here: pinning a tier the backend
  // never recommended invents an offer. No pinned tier -> no reco card, and
  // the reactivation block stands on its own.
  const pinnedTier: tLeadAgentSelectableTier | null =
    LEAD_AGENT_SELECTABLE_TIERS.find(
      (tier) => tier === acceptance?.suggestedTier,
    ) ?? null;

  // La fila compacta del banner, misma forma que en pre-aceptación: cada dato
  // se borra si el rollup no lo produjo, y la fila entera desaparece si no
  // sobrevive ninguno.
  const metrics = acceptance?.metrics;
  const miniStats = [
    {
      key: 'engagedLeads',
      value: formatMetricValue(metrics?.engagedLeadsCount ?? null),
      label: tOffer('miniStats.engagedLeads'),
    },
    {
      key: 'messages',
      value: formatMetricValue(metrics?.leadAgentMessagesCount ?? null),
      label: tOffer('miniStats.messages'),
    },
    {
      key: 'outOfHours',
      value: formatMetricValue(metrics?.outOfHoursAttendedLeadsCount ?? null),
      label: tOffer('miniStats.outOfHours'),
    },
  ].filter((stat) => stat.value !== null);

  const { price } = useLeadAgentTierPricing({
    workspaceId: acceptance?.workspaceId ?? '',
    tier: resolveSuggestedTier(acceptance?.suggestedTier),
    frequency: subscription?.frequency ?? ePlanFrequency.MONTHLY,
    currency: subscription?.currency ?? '',
    enabled: isExpiredForOwner && pinnedTier !== null,
  });

  function handleReactivated(result: tReactivateLeadAgentModalSuccess) {
    /*
     * SESSION-LOCAL resolution, adopted verbatim from the legacy container
     * (RET-1230's `isResolvedLocally` latch). `isExpiredForOwner` includes
     * `!isResolvedLocally`, so the alert disappears for the rest of the
     * session the moment the checkout reports success.
     *
     * Documented, not changed, in RET-1957 (RET-1956 review, follow-up 3).
     * Being honest about what it is: this is a CLIENT-SIDE claim that the
     * charge landed. It is NOT the backend confirming the subscription is
     * live again — the webhook that clears `leadAgentExpired` lags the
     * checkout, and the provider does not refetch here. Until the next full
     * load the alert's disappearance is a local assertion.
     *
     * It is accepted for one reason: the latch only ever HIDES, never shows.
     * The worst case is an owner who paid not being nagged for the rest of a
     * session that a reload corrects — the reverse (nagging someone who just
     * paid) is the failure that matters, and it is what the legacy banner
     * shipped this for. That trade differs from
     * `docs/solutions/logic-errors/localstorage-flag-set-before-backend-write-confirms.md`
     * because nothing here is PERSISTED: no cross-session state claims a
     * write that may not have happened, and the next load re-reads the truth.
     */
    markResolved(result.tier);
    addAlert({
      type: eAlertType.SUCCESS,
      message: t('detail.leadAgentExpired.reactivatedToast'),
    });
    // Two steps on purpose. The host refuses to close while this renderer's
    // nested modal is up, and it reads that lock from a ref refreshed on ITS
    // OWN render — so releasing the lock and asking to close in the same
    // commit would be swallowed and leave the detail floating over nothing.
    // `flushSync` lands the release first, then the request is honored.
    flushSync(() => {
      setIsReactivateOpen(false);
      onNestedModalChange(false);
    });
    onRequestClose();
  }

  async function handleDismiss() {
    if (isDismissingRef.current) return;
    isDismissingRef.current = true;
    try {
      await dismissExpired();
      // Persisted; the alert is gone for good, so nothing is left to read.
      onRequestClose();
    } catch {
      // dismissExpired already reverted its optimistic hide and rethrew; the
      // alert stays — just tell the Owner the persist failed.
      addAlert({ type: eAlertType.ERROR, message: tExpired('dismissError') });
    } finally {
      isDismissingRef.current = false;
    }
  }

  return (
    <div className="AlertDetail" data-testid="alert-detail-la-expired">
      <AlertDetailBody alert={props.alert} />

      {/* Lo que el agente HACÍA, antes del precio: es el argumento del botón
          de abajo. Solo para el dueño, que es quien puede reactivar. */}
      {isExpiredForOwner && miniStats.length > 0 ? (
        <div data-testid="alert-detail-expired-metrics">
          <p className="AlertDetail__Note">
            {t('detail.leadAgentExpired.metricsLabel')}
          </p>
          <div className="AlertDetail__MiniStats mt-1">
            {miniStats.map((stat) => (
              <span key={stat.key} className="AlertDetail__MiniStat">
                <b>{stat.value}</b> {stat.label}
              </span>
            ))}
          </div>
        </div>
      ) : null}

      {/* MLA-81 — the catalog price, whole, with nothing struck through: the
          reactivation charge is the full price of the tier. No price means the
          catalog has not landed, and the block does not render. */}
      {isExpiredForOwner && pinnedTier ? (
        <AlertOfferLine
          testId="alert-detail-expired-reco"
          label={tOffer('reco.label')}
          plan={tOffer('reco.plan', { tier: tOffer(`tier.${pinnedTier}`) })}
          price={price ?? null}
        />
      ) : null}

      {/* One row, as the prototype's `.actions`. The dismissal is the PERSISTED
          one — the only way out that writes what this alert's predicate reads —
          and stays the lightest thing in the row. */}
      {isExpiredForOwner ? (
        <div className="AlertDetail__Actions">
          <button
            type="button"
            className="AlertDetail__OptionCta AlertDetail__OptionCta--primary"
            onClick={() => setIsReactivateOpen(true)}
            data-testid="alert-detail-expired-reactivate"
          >
            {t('detail.leadAgentExpired.reactivateCta')}
          </button>
          <button
            type="button"
            className="AlertDetail__OptionCta AlertDetail__OptionCta--link"
            onClick={handleDismiss}
            data-testid="alert-detail-expired-dismiss"
          >
            {tExpired('owner.dismissLabel')}
          </button>
        </div>
      ) : null}

      {isReactivateOpen ? (
        <ReactivateLeadAgentModal
          onClose={() => setIsReactivateOpen(false)}
          onReactivated={handleReactivated}
        />
      ) : null}
    </div>
  );
}
